import React from 'react';
import { Skeleton } from '@aidkitorg/component-library';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  TooltipProps,
  ResponsiveContainer 
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { AccountWithColor } from '../AccountsPage';

type CustomTooltipProps = TooltipProps<ValueType, NameType> & {
  total: number;
};

const CustomTooltip = ({
  total,
  active,
  payload,
  label
}: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow text-sm">
        {payload.map((entry, index) => 
        {
          const percent = total > 0 ? ((entry.value as number / total) * 100).toFixed(1) : '0.0';
          const color = entry.payload.accountColor

          return (
            <div key={`item-${index}`} className="flex items-center gap-2">
              <div
                style={{
                  display: 'inline-block',
                  width: 15,
                  height: 15,
                  backgroundColor: color,
                }}
              />
              <div>
                <div>{entry.name}: </div>
                <div>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(entry.value as number)}
                  {' '}({percent}%)
                </div> 
              </div>
            </div>
          )
        })}
      </div>
    );
  }

  return null;
};

type CustomLegendProps = {
  data: Array<AccountWithColor>;
};

const CustomLegend = ({ data }: CustomLegendProps) => {
  return (
    <ul className="flex flex-col gap-2">
      {data.map((entry, index) => (
        <li key={index} className="flex items-center space-x-2 text-sm">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: entry.accountColor }} />
          <div className="break-normal">{entry.name}</div>
        </li>
      ))}
    </ul>
  );
};

type AccountsPieChartProps = {
  accountsWithColorData: Array<AccountWithColor>;
  isLoading: boolean,
};

export const AccountsPieChart = ({ accountsWithColorData, isLoading }: AccountsPieChartProps) => {
  const processedData = accountsWithColorData.map((account) => ({
    ...account,
    totalBalance: 0.01 * account.totalBalance,
  }))

  const totalBalanceSum = processedData.reduce((sum, acc) => sum + acc.totalBalance, 0);

  return (
    <div className="w-full flex flex-wrap items-center justify-center">
      <ResponsiveContainer width="50%" height={isLoading ? 300 : 350}>
        {isLoading ? (
          <Skeleton className="py-1" variant="block" size="auto" />
        ) : (
          <PieChart>
            <Pie
              data={processedData}
              dataKey="totalBalance"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="75%"
            >
              {processedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.accountColor} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip total={totalBalanceSum} />}
            />
          </PieChart>
        )}
      </ResponsiveContainer>
      <div>
        <CustomLegend data={processedData} />
      </div>
    </div>
  );
};
